import { useEffect, useState, type FormEvent } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { apiDelete, apiFetch, apiPost, friendlyError } from "../../api/client.js";
import { useAsync } from "../../lib/useAsync.js";
import { formatBytes } from "../customer/vmUtils.js";

interface Iso {
  volid: string;
  display_name: string;
  enabled: boolean;
  added_at: string;
}

interface StorageEntry {
  name: string;
  content: string;
}

interface StorageIso {
  volid: string;
  size_bytes: number;
  ctime: number;
}

const VOLID_RE = /^[a-zA-Z0-9_-]+:iso\/.+$/;

export function IsoAllowlist() {
  const { data, loading, error, refetch } = useAsync<Iso[]>(
    () => apiFetch<Iso[]>("/api/admin/isos"),
    []
  );
  const [addOpen, setAddOpen] = useState(false);
  const [mode, setMode] = useState<"browse" | "manual">("browse");

  // Browse state.
  const [node, setNode] = useState("");
  const [storage, setStorage] = useState<string | null>(null);
  const [storages, setStorages] = useState<StorageEntry[]>([]);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [isos, setIsos] = useState<StorageIso[]>([]);
  const [isoLoading, setIsoLoading] = useState(false);
  const [isoError, setIsoError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  // Manual-entry state.
  const [volid, setVolid] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const allowlistedVolids = new Set((data ?? []).map((i) => i.volid));

  function openAdd() {
    setMode("browse");
    setNode("");
    setStorage(null);
    setStorages([]);
    setStorageError(null);
    setIsos([]);
    setIsoError(null);
    setAdding(null);
    setVolid("");
    setDisplayName("");
    setErrors({});
    setAddOpen(true);
  }

  async function loadStorages(nodeVal: string) {
    const n = nodeVal.trim();
    if (!n) {
      setStorages([]);
      setStorage(null);
      return;
    }
    setStorageLoading(true);
    setStorageError(null);
    try {
      const list = await apiFetch<StorageEntry[]>(
        `/api/admin/storages?node=${encodeURIComponent(n)}`
      );
      setStorages(list);
      setStorage((cur) =>
        cur && list.some((s) => s.name === cur) ? cur : list[0]?.name ?? null
      );
    } catch {
      setStorageError("Can't reach Proxmox.");
      setStorages([]);
      setStorage(null);
    } finally {
      setStorageLoading(false);
    }
  }

  async function loadIsos(nodeVal: string, storageVal: string) {
    const n = nodeVal.trim();
    const s = storageVal.trim();
    if (!n || !s) {
      setIsos([]);
      return;
    }
    setIsoLoading(true);
    setIsoError(null);
    try {
      const list = await apiFetch<StorageIso[]>(
        `/api/admin/storage-isos?node=${encodeURIComponent(n)}&storage=${encodeURIComponent(s)}`
      );
      setIsos(list);
    } catch {
      setIsoError("Can't reach Proxmox.");
      setIsos([]);
    } finally {
      setIsoLoading(false);
    }
  }

  useEffect(() => {
    if (!addOpen || mode !== "browse") return;
    if (node.trim() && storage) {
      void loadIsos(node, storage);
    } else {
      setIsos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOpen, mode, node, storage]);

  async function addToAllowlist(iso: StorageIso) {
    setAdding(iso.volid);
    const file = iso.volid.split("/").pop() ?? iso.volid;
    const display = file.replace(/\.iso$/i, "");
    try {
      await apiPost("/api/admin/isos", {
        volid: iso.volid,
        display_name: display,
      });
      notifications.show({ color: "green", message: "ISO added" });
      refetch();
      if (node.trim() && storage) void loadIsos(node, storage);
    } catch (err) {
      notifications.show({ color: "red", message: friendlyError(err) });
    } finally {
      setAdding(null);
    }
  }

  async function submitManual(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!VOLID_RE.test(volid.trim())) {
      errs.volid = "Must look like <storage>:iso/<file>";
    }
    if (!displayName.trim()) errs.displayName = "Required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await apiPost("/api/admin/isos", {
        volid: volid.trim(),
        display_name: displayName.trim(),
      });
      notifications.show({ color: "green", message: "ISO added" });
      setAddOpen(false);
      refetch();
    } catch (err) {
      notifications.show({ color: "red", message: friendlyError(err) });
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete(iso: Iso) {
    modals.openConfirmModal({
      title: "Remove ISO",
      centered: true,
      children: (
        <Text size="sm">
          Remove <b>{iso.volid}</b> from the allowlist?
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await apiDelete(`/api/admin/isos/${encodeURIComponent(iso.volid)}`);
          notifications.show({ color: "green", message: "ISO removed" });
          refetch();
        } catch (err) {
          notifications.show({ color: "red", message: friendlyError(err) });
        }
      },
    });
  }

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Title order={2}>ISO Allowlist</Title>
        <Button onClick={openAdd}>Add ISO</Button>
      </Group>

      {error && (
        <Text c="red" mb="sm">
          {error}
        </Text>
      )}
      {data && data.length === 0 && (
        <Alert color="blue" variant="light" mb="sm">
          Allowlist is empty — customers can mount any ISO on their VM&rsquo;s node
          storage. Add ISOs below to enable curation.
        </Alert>
      )}
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>VOLID</Table.Th>
            <Table.Th>Display name</Table.Th>
            <Table.Th>Enabled</Table.Th>
            <Table.Th>Added</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed">Loading…</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && data && data.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed">No ISOs on the allowlist.</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {data?.map((iso) => (
            <Table.Tr key={iso.volid}>
              <Table.Td style={{ fontFamily: "monospace" }}>{iso.volid}</Table.Td>
              <Table.Td>{iso.display_name}</Table.Td>
              <Table.Td>{iso.enabled ? "yes" : "no"}</Table.Td>
              <Table.Td>{iso.added_at}</Table.Td>
              <Table.Td>
                <ActionIcon variant="default" color="red" size="sm" onClick={() => confirmDelete(iso)} title="Remove">
                  <Text size="xs">×</Text>
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title="Add ISO" size="lg" centered>
        <Stack gap="md">
          <Group gap="xs">
            <Button
              variant={mode === "browse" ? "light" : "subtle"}
              size="xs"
              onClick={() => setMode("browse")}
            >
              Browse PVE
            </Button>
            <Button
              variant={mode === "manual" ? "light" : "subtle"}
              size="xs"
              onClick={() => setMode("manual")}
            >
              Manual entry
            </Button>
          </Group>

          {mode === "browse" ? (
            <>
              <TextInput
                label="Node"
                placeholder="e.g. smunit4"
                value={node}
                onChange={(e) => {
                  setNode(e.currentTarget.value);
                  setStorage(null);
                  setStorages([]);
                  setIsos([]);
                }}
                onBlur={() => void loadStorages(node)}
              />
              {storageError && (
                <Alert color="red" variant="light" p="sm">
                  {storageError}
                </Alert>
              )}
              <Select
                label="Storage"
                placeholder={storageLoading ? "Loading…" : "Select storage"}
                value={storage}
                onChange={setStorage}
                data={storages.map((s) => ({ value: s.name, label: s.name }))}
                disabled={storageLoading || storages.length === 0}
              />
              {!storageLoading && !storageError && storages.length === 0 && node.trim() && (
                <Text c="dimmed" size="sm">
                  No ISO-capable storages on this node.
                </Text>
              )}

              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                  ISOs on storage
                </Text>
                {isoError ? (
                  <Alert color="red" variant="light" p="sm">
                    {isoError}
                  </Alert>
                ) : isoLoading ? (
                  <Text c="dimmed" size="sm">
                    Loading…
                  </Text>
                ) : !node.trim() || !storage ? (
                  <Text c="dimmed" size="sm">
                    Enter a node and pick a storage to list ISOs.
                  </Text>
                ) : isos.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    No ISOs on this storage.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {isos.map((iso) => {
                      const added = allowlistedVolids.has(iso.volid);
                      return (
                        <Group
                          key={iso.volid}
                          justify="space-between"
                          align="center"
                          wrap="nowrap"
                        >
                          <Stack gap={2} style={{ minWidth: 0 }}>
                            <Text size="sm" truncate>
                              {iso.volid}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatBytes(iso.size_bytes)}
                            </Text>
                          </Stack>
                          {added ? (
                            <Badge size="xs" variant="light" color="green">
                              added
                            </Badge>
                          ) : (
                            <Button
                              size="xs"
                              variant="default"
                              loading={adding === iso.volid}
                              onClick={() => void addToAllowlist(iso)}
                            >
                              Add to allowlist
                            </Button>
                          )}
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </>
          ) : (
            <form onSubmit={submitManual}>
              <Stack gap="sm">
                <TextInput
                  label="VOLID"
                  placeholder="local:iso/debian-12.iso"
                  value={volid}
                  onChange={(e) => setVolid(e.currentTarget.value)}
                  error={errors.volid}
                />
                <TextInput
                  label="Display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  error={errors.displayName}
                />
                <Button type="submit" loading={submitting} mt="xs">
                  Add
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Modal>
    </Box>
  );
}
